import React from 'react';
import Footer from '../components/Footer';
import LegalShell, { LegalNav, H2, H3 } from '../components/LegalShell';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#020202] text-white">
      <LegalNav />
      <main className="container mx-auto max-w-3xl px-6 py-12 md:py-16">
        <LegalShell title="Privacy Policy" lastUpdated="February 2026">
          <p>
            ResetProtocol ("we," "us," or "our") respects your privacy. This Privacy Policy
            explains what information we collect, how we use it, and the rights you have over
            your data when you use the ResetProtocol mobile applications and the website at
            <a href="https://resetprotocol.app" className="text-[#a83a4a] hover:underline mx-1">resetprotocol.app</a>
            (collectively, the "Service").
          </p>

          {/* ─── DATA WE COLLECT ───────────────────────────────── */}
          <H2>Data We Collect</H2>
          <p>ResetProtocol may collect:</p>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#a83a4a]/60">
            <li>Account information (email address, authentication identifiers)</li>
            <li>Recovery progress data</li>
            <li>App usage analytics</li>
            <li>Subscription status</li>
            <li>Device notification tokens</li>
          </ul>
          <p className="font-semibold text-white/80">We do not sell personal data to third parties.</p>

          <H3>Account &amp; Authentication</H3>
          <p>
            When you create an account we collect your email address, a hashed password, an
            anonymous display name (which you choose), and the timestamp of account creation.
            If you sign in with Google we receive your email address and basic profile in line
            with the permissions you grant.
          </p>

          <H3>Recovery &amp; Usage Data</H3>
          <p>
            To provide the core experience we store the data you create inside the app:
            check-ins, streak history, relapse logs, urge events, journal entries, AI Coach
            conversations, badges and progression, and notification preferences. This data is
            tied to your account and never sold.
          </p>

          <H3>Analytics</H3>
          <p>
            We use Google Analytics and an internal event log to understand how the Service is
            used. This includes pages visited, button clicks, referrer source, device type,
            approximate country, and session duration. We do not link this data to your
            real-world identity.
          </p>

          <H3>Push Notifications</H3>
          <p>
            If you enable notifications we use Expo Push and the Apple Push Notification Service
            / Firebase Cloud Messaging to deliver streak reminders, milestone alerts, and
            re-engagement messages. You can disable these in your device settings at any time.
          </p>

          <H3>AI Coach Interactions</H3>
          <p>
            Messages you send to the AI Coach are processed by our LLM provider to generate a
            reply. We retain a short conversation history per user to maintain context. We do
            not train third-party models on your conversations.
          </p>

          {/* ─── SUBSCRIPTIONS ───────────────────────────────── */}
          <H2>Subscriptions</H2>
          <p>
            Premium features are offered through auto-renewing subscriptions managed by
            <strong> Apple App Store</strong> and <strong>Google Play</strong>.
          </p>
          <p>
            Subscriptions can be managed or cancelled through your device account settings. We
            never see or store your full payment card number; subscription status is reconciled
            through <strong>RevenueCat</strong>, which acts as a subscription processor on our
            behalf.
          </p>

          {/* ─── HOW WE USE DATA ───────────────────────────────── */}
          <H2>How We Use Your Data</H2>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#a83a4a]/60">
            <li>To create and secure your account and verify you at sign-in.</li>
            <li>To deliver the core recovery support features.</li>
            <li>To send transactional and opt-in retention emails &amp; push notifications.</li>
            <li>To process subscriptions and grant access to paid features.</li>
            <li>To improve the Service through aggregate, de-identified analytics.</li>
            <li>To prevent fraud and enforce our Terms.</li>
          </ul>

          {/* ─── THIRD PARTIES ───────────────────────────────── */}
          <H2>Third-Party Services</H2>
          <p>We rely on the following processors. Each is bound by its own privacy policy:</p>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#a83a4a]/60">
            <li><strong>Apple</strong> — App Store distribution and subscription billing.</li>
            <li><strong>Google</strong> — Google Play distribution, billing, and Google Analytics.</li>
            <li><strong>RevenueCat</strong> — subscription orchestration and entitlement.</li>
            <li><strong>Expo</strong> — mobile build infrastructure and push delivery.</li>
            <li><strong>Resend</strong> — transactional email delivery.</li>
            <li><strong>OpenAI</strong> — AI Coach text generation.</li>
            <li><strong>Stripe</strong> — backup web checkout (where applicable).</li>
          </ul>

          {/* ─── ACCOUNT DELETION (Google Play compliant) ───────────────────────────────── */}
          <H2>Account Deletion</H2>
          <p>
            Users can permanently delete their ResetProtocol account directly inside the app.
          </p>

          <H3>To delete your account:</H3>
          <ol className="list-decimal pl-6 space-y-2 marker:text-[#a83a4a]/60">
            <li>Open the ResetProtocol app</li>
            <li>Go to <em>Settings</em></li>
            <li>Select <em>"Delete Account"</em></li>
            <li>Confirm permanent deletion</li>
          </ol>

          <H3>When an account is deleted:</H3>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#a83a4a]/60">
            <li>Personal account information is permanently removed</li>
            <li>Associated recovery data and analytics are deleted</li>
            <li>Subscription management remains handled through Apple App Store or Google Play subscriptions</li>
            <li>Some limited records may be retained temporarily where legally required for fraud prevention or payment compliance</li>
          </ul>

          <p>
            If you cannot access your account, you may also request deletion by contacting:
            <br />
            <a href="mailto:support@resetprotocol.app" className="text-[#a83a4a] hover:underline">
              support@resetprotocol.app
            </a>
          </p>

          {/* ─── DATA RETENTION ───────────────────────────────── */}
          <H2>Data Retention</H2>
          <p>
            We retain account and usage data for as long as your account is active. After
            deletion we erase your personal data within 30 days, except where we are legally
            required to retain it (for example transaction records for tax or fraud-prevention
            compliance, kept in anonymized form for up to 7 years).
          </p>

          {/* ─── YOUR RIGHTS ───────────────────────────────── */}
          <H2>Your Rights</H2>
          <p>You can access, export, correct, or delete your data at any time:</p>
          <ul className="list-disc pl-6 space-y-2 marker:text-[#a83a4a]/60">
            <li>Open the app → Settings → <em>Delete Account</em>; or</li>
            <li>
              Email <a href="mailto:support@resetprotocol.app" className="text-[#a83a4a] hover:underline">support@resetprotocol.app</a>
              {' '}from your registered address.
            </li>
          </ul>
          <p>
            If you are in the EU/UK you also have the right to lodge a complaint with your
            local data protection authority.
          </p>

          {/* ─── CHILDREN ───────────────────────────────── */}
          <H2>Children</H2>
          <p>
            ResetProtocol is intended for users aged 18 and over. We do not knowingly collect
            data from children under 13. If you believe a child has provided us data, contact
            us and we will delete it.
          </p>

          {/* ─── SECURITY ───────────────────────────────── */}
          <H2>Security</H2>
          <p>
            We use industry-standard practices to protect your data, including TLS in transit,
            hashed credentials at rest, and access controls on our infrastructure. No system is
            perfectly secure, however, and we cannot guarantee absolute confidentiality.
          </p>

          {/* ─── CHANGES ───────────────────────────────── */}
          <H2>Changes to this Policy</H2>
          <p>
            We may update this Privacy Policy from time to time. Material changes will be
            announced in the app or by email. Continued use of the Service after a change means
            you accept the updated policy.
          </p>

          {/* ─── CONTACT ───────────────────────────────── */}
          <H2>Contact</H2>
          <p>
            Questions, requests, or complaints? Email{' '}
            <a href="mailto:support@resetprotocol.app" className="text-[#a83a4a] hover:underline">support@resetprotocol.app</a>.
          </p>
        </LegalShell>
      </main>
      <Footer />
    </div>
  );
}
